import React, { useEffect, useMemo } from 'react';
import { Alert, Box, IconButton, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { FieldEntryDTO, FieldType, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import {
  DOCUMENTS_RECEIVED_SECTION_CONFIG,
  DOCUMENTS_RECEIVED_FIELD_NAMES,
  ALL_DOCS_DEFAULT_COMMENT,
  LOAN_DOCS_COMMENT_NO_TEXT,
  LOAN_DOCS_COMMENT_YES_TEXT,
  DOCUMENTS_RECEIVED_INTRO_ALL_NO_TEXT,
  DOCUMENTS_RECEIVED_INTRO_SOME_RECEIVED_TEXT,
} from './sections.config';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { FieldRow } from './FieldRow';
import { LastMonthFieldToggle } from './LastMonthFieldToggle';
import { DocumentsReceivedTable } from './DocumentsReceivedTable';

interface DocumentsReceivedSectionProps {
  magicReportId: string;
}

const INTRO_COMMENT_FIELD = 'DocumentsReceivedIntroComment';
const ALL_DOCS_COMMENT_FIELD = 'AllDocsComment';
const LOAN_DOCS_COMMENT_FIELD = 'LoanDocsComment';
const LOAN_DOCS_STATUS_FIELD = 'LoanDocumentsHardCost';
const COMMENT_ON_LOAN_DOCS_FIELD = 'CommentOnLoanDocs';
const COMMENTS_FIELD = 'Comments';
const DOC_TYPES_FIELD = 'DocumentsReceivedDocTypes';
const MAX_DOC_TYPES = 18;

const DocumentsReceivedSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const commentOnLoanDocs = getField(COMMENT_ON_LOAN_DOCS_FIELD)?.value === 'true';
  const loanDocsValue = getField(LOAN_DOCS_STATUS_FIELD)?.value || 'No';
  const loanDocsCommentValue = getField(LOAN_DOCS_COMMENT_FIELD)?.value ?? '';

  useEffect(() => {
    if (!commentOnLoanDocs || loanDocsValue === 'N/A') {
      if (loanDocsCommentValue === LOAN_DOCS_COMMENT_YES_TEXT || loanDocsCommentValue === LOAN_DOCS_COMMENT_NO_TEXT) {
        handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: '' });
      }
      return;
    }

    const targetText = loanDocsValue === 'Yes' ? LOAN_DOCS_COMMENT_YES_TEXT : LOAN_DOCS_COMMENT_NO_TEXT;
    const isDefaultOrEmpty =
      !loanDocsCommentValue || loanDocsCommentValue === LOAN_DOCS_COMMENT_YES_TEXT || loanDocsCommentValue === LOAN_DOCS_COMMENT_NO_TEXT;

    if (isDefaultOrEmpty && loanDocsCommentValue !== targetText) {
      handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: targetText });
    }
  }, [commentOnLoanDocs, loanDocsValue]);

  const showLoanDocsError = commentOnLoanDocs && loanDocsValue === 'N/A';
  const allDocsCommentField = getField(ALL_DOCS_COMMENT_FIELD);
  const loanDocsCommentField = getField(LOAN_DOCS_COMMENT_FIELD);
  const commentsField = getCommentsField(data, DOCUMENTS_RECEIVED_SECTION_CONFIG.config);

  const showAllDocsAutoComment = true;
  const showLoanDocsAutoComment = commentOnLoanDocs && loanDocsValue !== 'N/A';
  const showAutomatedComments = showAllDocsAutoComment || showLoanDocsAutoComment;

  const docTypesField = getField(DOC_TYPES_FIELD);

  const allDocsNo = useMemo(() => {
    const entries = docTypesField?.entries || [];
    if (entries.length === 0) return false;
    return entries.every(entry => {
      try {
        const parsed = JSON.parse(entry.value || '');
        const status = getField(parsed.fieldName)?.value;
        return status === 'No' || status === 'N/A';
      } catch {
        return false;
      }
    });
  }, [docTypesField, data, getField]);

  const introTargetText = allDocsNo ? DOCUMENTS_RECEIVED_INTRO_ALL_NO_TEXT : DOCUMENTS_RECEIVED_INTRO_SOME_RECEIVED_TEXT;

  useEffect(() => {
    const currentValue = getField(INTRO_COMMENT_FIELD)?.value ?? '';
    const isDefaultOrEmpty =
      !currentValue ||
      currentValue === DOCUMENTS_RECEIVED_INTRO_ALL_NO_TEXT ||
      currentValue === DOCUMENTS_RECEIVED_INTRO_SOME_RECEIVED_TEXT;

    if (isDefaultOrEmpty && currentValue !== introTargetText) {
      handleFieldChange(INTRO_COMMENT_FIELD, { value: introTargetText });
    }
  }, [allDocsNo]);

  const handleRefreshIntroComment = () => {
    handleFieldChange(INTRO_COMMENT_FIELD, { value: introTargetText });
  };

  const layout = useMemo(
    () => ({
      singleColumn: [
        {
          fieldName: INTRO_COMMENT_FIELD,
          type: 'text-with-refresh' as const,
          label: '',
          onRefresh: handleRefreshIntroComment,
        },
        {
          fieldName: DOC_TYPES_FIELD,
          type: 'custom-component' as const,
          label: '',
          renderComponent: () => (
            <DocumentsReceivedTable
              docTypesField={docTypesField}
              handleFieldChange={handleFieldChange}
              getField={getField}
              maxDocTypes={MAX_DOC_TYPES}
            />
          ),
        },
      ],
    }),
    [docTypesField, handleFieldChange, getField, introTargetText],
  );

  const handleLoanDocsCommentStatusChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue === null) {
      return;
    }

    const shouldComment = newValue === 'Commenting on Loan Docs';

    // Only update if the value is actually changing
    if (commentOnLoanDocs !== shouldComment) {
      handleFieldChange(COMMENT_ON_LOAN_DOCS_FIELD, { value: shouldComment ? 'true' : 'false' });

      if (!shouldComment) {
        handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: '' });
        return;
      }

      if (loanDocsValue === 'N/A') {
        return;
      }

      const targetText = loanDocsValue === 'Yes' ? LOAN_DOCS_COMMENT_YES_TEXT : LOAN_DOCS_COMMENT_NO_TEXT;
      handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: targetText });
    }
  };

  const handleRefreshAllDocsComment = () => {
    handleFieldChange(ALL_DOCS_COMMENT_FIELD, { value: ALL_DOCS_DEFAULT_COMMENT });
  };

  const handleRefreshLoanDocsComment = () => {
    if (loanDocsValue === 'N/A') return;
    const targetText = loanDocsValue === 'Yes' ? LOAN_DOCS_COMMENT_YES_TEXT : LOAN_DOCS_COMMENT_NO_TEXT;
    handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: targetText });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ mt: -3 }}>
        <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ width: 21, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <ToggleButtonGroup
                exclusive
                size='medium'
                value={commentOnLoanDocs ? 'Commenting on Loan Docs' : 'Not commenting on Loan Docs'}
                onChange={handleLoanDocsCommentStatusChange}
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              >
                <ToggleButton value='Not commenting on Loan Docs'>Not commenting on Loan Docs</ToggleButton>
                <ToggleButton value='Commenting on Loan Docs'>Commenting on Loan Docs</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box sx={{ width: 30, flexShrink: 0 }} />
        </Box>
      </Box>

      {showLoanDocsError && (
        <Alert severity='warning' sx={{ mt: 1 }}>
          Loan documents marked as N/A cannot have an automated comment. Please select Yes or No to continue.
        </Alert>
      )}
      {commentsField && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showAutomatedComments && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {showLoanDocsAutoComment && (
                <Box>
                  <Box sx={{ mb: 0.5 }}>
                    <LastMonthFieldToggle
                      lastMonthValue={loanDocsCommentField?.lastMonth}
                      label='Loan Documents Comment'
                      reserveTrailingActionSpace
                    />
                  </Box>
                  <FieldRow
                    addToActionItems={loanDocsCommentField?.addToActionItems}
                    onBookmarkToggle={() => {
                      if (loanDocsCommentField) {
                        handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { addToActionItems: !loanDocsCommentField.addToActionItems });
                      }
                    }}
                    alignStart
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={loanDocsCommentField?.value || ''}
                        onChange={e => handleFieldChange(LOAN_DOCS_COMMENT_FIELD, { value: e.target.value })}
                        placeholder='Loan documents comment...'
                      />
                      <IconButton
                        size='small'
                        onClick={handleRefreshLoanDocsComment}
                        sx={{ color: 'primary.main', mt: 0.5 }}
                        title='Restore default comment'
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Box>
                  </FieldRow>
                </Box>
              )}
              {showAllDocsAutoComment && (
                <Box>
                  <Box sx={{ mb: 0.5 }}>
                    <LastMonthFieldToggle
                      lastMonthValue={allDocsCommentField?.lastMonth}
                      label='All Documents Comment'
                      reserveTrailingActionSpace
                    />
                  </Box>
                  <FieldRow
                    addToActionItems={allDocsCommentField?.addToActionItems}
                    onBookmarkToggle={() => {
                      if (allDocsCommentField) {
                        handleFieldChange(ALL_DOCS_COMMENT_FIELD, { addToActionItems: !allDocsCommentField.addToActionItems });
                      }
                    }}
                    alignStart
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={allDocsCommentField?.value || ''}
                        onChange={e => handleFieldChange(ALL_DOCS_COMMENT_FIELD, { value: e.target.value })}
                        placeholder='All documents comment...'
                      />
                      <IconButton
                        size='small'
                        onClick={handleRefreshAllDocsComment}
                        sx={{ color: 'primary.main', mt: 0.5 }}
                        title='Restore default comment'
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Box>
                  </FieldRow>
                </Box>
              )}
            </Box>
          )}
          <CommentsSection
            commentsField={commentsField}
            onChange={updated => handleFieldChange(COMMENTS_FIELD, updated)}
            title='Comments'
            onBook={onBook}
          />
        </Box>
      )}
    </Box>
  );
};

export const DocumentsReceivedSection: React.FC<DocumentsReceivedSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={DOCUMENTS_RECEIVED_SECTION_CONFIG}
      customization={{
        onDataTransform: data => {
          const fields = [...data.fields];
          const existingDocTypesField = fields.find(f => f.fieldName === DOC_TYPES_FIELD);

          if (existingDocTypesField && existingDocTypesField.entries && existingDocTypesField.entries.length > 0) {
            return data;
          }

          const defaultEntries: FieldEntryDTO[] = DOCUMENTS_RECEIVED_FIELD_NAMES.map((fieldName, index) => {
            const label =
              DOCUMENTS_RECEIVED_SECTION_CONFIG.layout &&
              ([
                ...(DOCUMENTS_RECEIVED_SECTION_CONFIG.layout.leftColumn || []),
                ...(DOCUMENTS_RECEIVED_SECTION_CONFIG.layout.rightColumn || []),
              ].find(cfg => cfg.fieldName === fieldName)?.label ||
                fieldName);

            return {
              value: JSON.stringify({ fieldName, label }),
              addToActionItems: false,
              order: index,
            };
          });

          if (existingDocTypesField) {
            const updatedFields = fields.map(f =>
              f.fieldName === DOC_TYPES_FIELD ? { ...f, fieldType: FieldType.TextArray, entries: defaultEntries } : f,
            );
            return { ...data, fields: updatedFields };
          }

          return {
            ...data,
            fields: [
              ...fields,
              {
                fieldName: DOC_TYPES_FIELD,
                fieldType: FieldType.TextArray,
                entries: defaultEntries,
                addToActionItems: false,
              },
            ],
          };
        },
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <DocumentsReceivedSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
